import { get } from 'lodash'

import { getSpaceId } from '@/store/contextSubscriber'

import fetch from './fetch'

/**
 * Get the upload request object from the assets V2 API
 *
 * @param {object} file - The file to upload, can be used directly from an input element
 * @param {string} file.name - The name of the file
 * @param {string} file.type - The content type of the file
 * @param {string} [assetType='image'] - The type of the asset (image or attachment)
 * @param {object} [options] - The additional options to pass in the body
 * @param {object} [options.transformCriteria] - The transformation to this asset
 * @param {number} [options.transformCriteria.width] - The only transformation criteria we permit at this time
 * @param {string} [onBehalfOfUserId=null] - The userId which the current user is uploading on behalf of
 * @throws {Error} Will throw an exception if the response comes back negative (errors object or non-200)
 * @returns {Promise<object>}
 */
const fetchUploadRequestV2 = async (file: any, assetType = 'image', options?: any, onBehalfOfUserId?: any) => {
  const uploadFormResponse = await fetch(
    '',
    {
      method: 'POST',
      body: {
        fileName: file.name,
        contentType: file.type,
        ...(assetType ? { assetType } : null),
        ...(onBehalfOfUserId ? { onBehalfOfUserId } : null),
        ...options
      },

      headers: { 'x-workgrid-space': getSpaceId() }
    },
    { service: 'assetsV2' }
  )

  if (get(uploadFormResponse, 'errors', []).length > 0) {
    throw new Error(uploadFormResponse.errors[0].message)
  }

  return uploadFormResponse
}

/**
 * Upload the asset to S3 using the signed postData
 *
 * @typedef {object} UploadRequestResponse
 * @property {Promise} promise - await this to catch any upload exceptions
 * @property {AbortController} abortController - Use `.abort()` to abort the upload
 *
 * @param {object} signedPostData - The postData from the signed asset request
 * @param {string} signedPostData.url - The URL to use when posting the upload <form>
 * @param {object} signedPostData.fields - The fields to add to the upload <form>
 * @param {Blob|any} file - The file as provided from a file input element
 * @param {string} file.getName - The name
 * @returns {UploadRequestResponse}
 **/
const uploadFileWithSignedUploadRequest = (signedPostData: any = {}, file: any) => {
  if (!file) throw new Error('No image!')

  const formFields = signedPostData.fields
  const formUrl = signedPostData.url

  if (!formFields || !formUrl) throw new Error('Invalid Post Data')

  const formData = new FormData()

  // Build the form entries
  Object.entries(formFields).forEach(([key, value]) => {
    // @ts-expect-error
    formData.append(key, value)
  })

  // Attach the actual file itself, we shouldn't need to turn it into a buffer
  formData.append('file', file)

  // Make this request abort-able
  const abortController = new AbortController()

  // Using the actual window.fetch since this is a raw form post
  const response = window
    .fetch(formUrl, {
      method: 'post',
      body: formData,
      signal: abortController.signal
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(response.statusText)
      }
    })
    .catch(error => {
      if (error.name !== 'AbortError') {
        throw error
      }
    })

  return { promise: response, abortController }
}

/**
 * Request to have an asset deleted.  This will eat any exceptions so you can fire and forget
 *
 * @param {string} assetId - Asset ID to delete
 * @param {object} [options]
 * @param {boolean} [options.hardDelete = false] - Actually delete the asset from S3 (for V2)
 * @return {Promise<void>}
 */
const requestDeleteAsset = async (assetId: any, { hardDelete = false } = {}) => {
  try {
    const response = await fetch(
      `/${assetId}`,
      {
        method: 'DELETE',
        headers: { 'x-workgrid-space': getSpaceId() },
        body: hardDelete === true ? { hardDelete } : undefined
      },
      { service: `assetsV2` }
    )
    if (!response.ok) {
      //TODO Sentry??
    }
  } catch {
    // NOM NOM NOM! SENTRY??
  }
}

export { uploadFileWithSignedUploadRequest, requestDeleteAsset, fetchUploadRequestV2 }
